import { Customer } from "./CustomerTypes";

export const isPatientDirectoryRow = (customer: Customer): boolean =>
	customer.user_type === "patient";

/** CU-14ygtkj7wuy — only a CURRENT customer can be converted; a patient row has no reverse action. */
export const isCustomerAccountRow = (customer: Customer): boolean =>
	customer.user_type === "customer";

/** CU-86d44j2ac — generic user storage only; no patient_info. */
export interface CustomerProfileDraft {
	first_name: string;
	middle_name: string;
	last_name: string;
	username: string;
	email: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	country: string;
	postal_code: string;
	date_of_birth: string | null;
	gender: string;
	sex: string;
	height: string;
	weight: string;
	profile_picture: string;
	has_one_name_only: boolean;
}

const text = (value: unknown): string =>
	value === null || value === undefined ? "" : String(value);

export const customerProfileDraft = (customer: Customer): CustomerProfileDraft => ({
	first_name: text(customer.first_name),
	middle_name: text(customer.middle_name),
	last_name: text(customer.last_name),
	username: text(customer.username),
	email: text(customer.email),
	phone: text(customer.phone),
	address: text(customer.address),
	city: text(customer.city),
	state: text(customer.state),
	country: text(customer.country),
	postal_code: text(customer.postal_code),
	// The save payload carries a UTC-midnight Date; String(Date) would yield "Fri May 04", never equal to the draft.
	date_of_birth: customer.date_of_birth
		? (customer.date_of_birth instanceof Date ? customer.date_of_birth.toISOString() : String(customer.date_of_birth)).slice(0, 10)
		: null,
	gender: text(customer.gender),
	sex: text(customer.sex),
	height: text(customer.height),
	weight: text(customer.weight),
	profile_picture: text(customer.profile_picture),
	has_one_name_only: Boolean(customer.has_one_name_only),
});

/**
 * CU-14ygtkj7wuy — unsaved profile edits are the draft's difference from a draft rebuilt from the record, so
 * null/empty normalisation and non-draft account fields (user_type, membership, capability) never count.
 */
export const hasPendingProfileChanges = (draft: CustomerProfileDraft, customer: Customer): boolean => {
	const persisted = customerProfileDraft(customer);

	return (Object.keys(persisted) as (keyof CustomerProfileDraft)[]).some((key) => draft[key] !== persisted[key]);
};

/**
 * CU-86d44j2ac — body for the CANONICAL patient update: the complete primary-name tuple travels together
 * so the server-side name lifecycle sees one whole replacement; patient_info is never sent.
 */
export const buildPatientRowUpdate = (draft: CustomerProfileDraft): Record<string, unknown> => ({
	first_name: draft.first_name,
	middle_name: draft.middle_name,
	last_name: draft.last_name,
	has_one_name_only: draft.has_one_name_only,
	username: draft.username,
	email: draft.email,
	phone: draft.phone,
	address: draft.address,
	city: draft.city,
	state: draft.state,
	country: draft.country,
	postal_code: draft.postal_code,
	date_of_birth: draft.date_of_birth,
	gender: draft.gender,
	sex: draft.sex,
	height: draft.height,
	weight: draft.weight,
});

export const buildCustomerOnlyUpdate = (draft: CustomerProfileDraft): Record<string, unknown> => ({
	first_name: draft.first_name,
	middle_name: draft.middle_name,
	last_name: draft.last_name,
	username: draft.username,
	email: draft.email,
	phone: draft.phone,
	address: draft.address,
	city: draft.city,
	state: draft.state,
	country: draft.country,
	postal_code: draft.postal_code,
	date_of_birth: draft.date_of_birth,
	gender: draft.gender,
	sex: draft.sex,
	height: draft.height,
	weight: draft.weight,
});

export const customerDisplayName = (customer: Customer): string =>
	[customer.first_name, customer.middle_name, customer.last_name]
		.filter((value) => Boolean(value && String(value).trim()))
		.join(" ") || "Unnamed customer";

export const customerMoney = new Intl.NumberFormat("en-AU", {
	style: "currency",
	currency: "AUD",
});
